import { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Modal, inputCls, btnTurq, btnGhost, EmptyState } from "../../components/bits";
import { RefreshCw, StickyNote, BellRing, ListChecks, Search } from "lucide-react";

export default function SupDashboard() {
  const [data, setData] = useState(null);
  const [sectorF, setSectorF] = useState("");
  const [q, setQ] = useState("");
  const [modal, setModal] = useState(null);
  const [text, setText] = useState("");
  const [notes, setNotes] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/supervisor/overview").then((r) => setData(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const schools = useMemo(() => (data?.schools || []).filter((s) =>
    (!sectorF || s.sector === sectorF) && (!q || s.name.includes(q))), [data, sectorF, q]);

  const send = async (kind) => {
    if (!text.trim()) {
      toast.error(kind === "note" ? "اكتبي الملاحظة أولاً" : "اكتبي نص التنبيه أولاً");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/supervisor/schools/${modal.school.id}/${kind === "note" ? "notes" : "alert"}`, { text });
      toast.success(kind === "note" ? "سُجّلت الملاحظة — تظهر للمدير العام" : r.data.message);
      setModal(null);
      setText("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const openNotes = async (s) => {
    setModal({ mode: "notes", school: s });
    setNotes([]);
    try {
      const { data } = await api.get(`/supervisor/schools/${s.id}/notes`);
      setNotes(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const stats = data?.stats;
  const sectors = (data?.supervisor.sectors || []).filter((s) => s !== "جميع المدارس");

  return (
    <div data-testid="sup-dashboard" className="space-y-6">
      <PageHeader title={`أهلاً ${data?.supervisor.name || ""} — لوحة المتابعة`}
        sub="مدارس قطاعاتك: المؤشرات والساعات والفرص وبيانات التواصل مع المنسقات"
        actions={<button data-testid="sup-refresh-btn" onClick={load} className={btnGhost}><RefreshCw className="w-4 h-4" /> تحديث</button>} />

      {stats && (
        <div data-testid="sup-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-surface rounded-2xl border border-brd shadow-md p-4 text-center">
            <div data-testid="sup-stat-schools" className="font-head font-black text-2xl text-turq-900 dark:text-turq-300">{stats.schools}</div>
            <div className="text-[11px] text-mutext">مدرسة في نطاقي</div>
          </div>
          <div className="bg-surface rounded-2xl border border-brd shadow-md p-4 text-center">
            <div data-testid="sup-stat-linked" className="font-head font-black text-2xl text-turq-900 dark:text-turq-300">{stats.linked}</div>
            <div className="text-[11px] text-mutext">مرتبطة بمنسقة</div>
          </div>
          <div className="bg-surface rounded-2xl border border-brd shadow-md p-4 text-center">
            <div data-testid="sup-stat-hours" className="font-head font-black text-2xl text-coral-600">{stats.hours}</div>
            <div className="text-[11px] text-mutext">ساعة تطوعية</div>
          </div>
          <div className="bg-surface rounded-2xl border border-brd shadow-md p-4 text-center">
            <div data-testid="sup-stat-pct" className="font-head font-black text-2xl text-turq-600">{stats.avg_pct}%</div>
            <div className="text-[11px] text-mutext">متوسط مؤشر التمكين (الثانوية)</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap items-center">
        <div data-testid="sup-sector-chips" className="flex gap-1.5 flex-wrap flex-1">
          {["", ...sectors].map((s) => (
            <button key={s || "all"} data-testid={`sup-sector-chip-${s || "all"}`} onClick={() => setSectorF(s)}
              className={`rounded-full px-4 py-2 text-[12px] font-bold transition-all ${sectorF === s
                ? "bg-turq-600 text-white shadow-md" : "bg-surface text-mutext border border-brd hover:scale-105"}`}>
              {s || "كل القطاعات"} ({(data?.schools || []).filter((x) => !s || x.sector === s).length})
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-mutext pointer-events-none" />
          <input data-testid="sup-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="ابحثي باسم المدرسة…" className={`${inputCls} pr-9`} />
        </div>
      </div>

      {schools.length === 0 ? (
        <EmptyState testid="sup-empty" text={data ? "لا توجد مدارس مطابقة في نطاق إشرافك" : "جارٍ التحميل …"} />
      ) : (
        <div className="bg-surface rounded-2xl border border-brd shadow-md overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="bg-turq-900 text-white">
                <th className="text-right px-4 py-3 font-head text-[11px]">المدرسة</th>
                <th className="text-right px-4 py-3 font-head text-[11px]">القطاع</th>
                <th className="text-right px-4 py-3 font-head text-[11px]">المؤشر / الساعات</th>
                <th className="text-right px-4 py-3 font-head text-[11px]">الفرص</th>
                <th className="text-right px-4 py-3 font-head text-[11px]">المنسقة</th>
                <th className="text-right px-4 py-3 font-head text-[11px]">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {schools.map((s) => (
                <tr key={s.id} data-testid={`sup-school-row-${s.id.slice(0, 8)}`} className="border-t border-brd/50 hover:bg-turq-100/30 dark:hover:bg-surface2 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-bold">{s.name}</div>
                    <div className="text-[10px] text-mutext">{s.stage}{s.manual ? " · إسناد يدوي" : ""}</div>
                  </td>
                  <td className="px-4 py-3">
                    {s.sector ? <span className="text-[11px] font-bold bg-beige-600 dark:bg-surface2 rounded-full px-2.5 py-1">{s.sector}</span> : <span className="text-[11px] text-mutext">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {s.mode === "staff" ? (
                      <span data-testid={`sup-school-hours-${s.id.slice(0, 8)}`} className="text-[12px] font-bold">{s.unique_students} منسوب · {s.hours} ساعة</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span data-testid={`sup-school-pct-${s.id.slice(0, 8)}`} className={`font-head font-black text-base ${(s.pct || 0) >= 90 ? "text-turq-600" : "text-coral-600"}`}>{s.pct ?? 0}%</span>
                        <span className="text-[10px] text-mutext">{s.unique_students}/{s.total_students} · {s.hours} س</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-bold">{s.opportunities}</td>
                  <td className="px-4 py-3">
                    {s.coordinator ? (
                      <div className="text-[11px] space-y-0.5">
                        <div className="font-bold">{s.coordinator.name}</div>
                        <div className="text-mutext" dir="ltr">{s.coordinator.phone || "—"}</div>
                        <div className="text-mutext" dir="ltr">{s.coordinator.email}</div>
                      </div>
                    ) : (
                      <span data-testid={`sup-school-nocoord-${s.id.slice(0, 8)}`} className="text-[11px] font-bold text-coral-600">لا توجد منسقة</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button data-testid={`sup-note-btn-${s.id.slice(0, 8)}`} title="تسجيل ملاحظة متابعة"
                        onClick={() => { setModal({ mode: "note", school: s }); setText(""); }}
                        className="w-8 h-8 rounded-lg bg-turq-100 dark:bg-surface2 flex items-center justify-center hover:scale-110 transition-transform">
                        <StickyNote className="w-3.5 h-3.5 text-turq-900 dark:text-turq-300" />
                      </button>
                      <button data-testid={`sup-alert-btn-${s.id.slice(0, 8)}`} title="تنبيه المنسقة"
                        disabled={!s.coordinator}
                        onClick={() => { setModal({ mode: "alert", school: s }); setText(""); }}
                        className="w-8 h-8 rounded-lg bg-coral-100 dark:bg-surface2 flex items-center justify-center hover:scale-110 transition-transform disabled:opacity-40">
                        <BellRing className="w-3.5 h-3.5 text-coral-900 dark:text-coral-300" />
                      </button>
                      <button data-testid={`sup-notes-list-${s.id.slice(0, 8)}`} title={`سجل الملاحظات (${s.notes_count})`}
                        onClick={() => openNotes(s)}
                        className="w-8 h-8 rounded-lg bg-beige-600 dark:bg-surface2 flex items-center justify-center hover:scale-110 transition-transform relative">
                        <ListChecks className="w-3.5 h-3.5 text-turq-900 dark:text-turq-300" />
                        {s.notes_count > 0 && <span className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-coral-600 text-white text-[9px] font-bold flex items-center justify-center">{s.notes_count}</span>}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={modal?.mode === "note" || modal?.mode === "alert"} onClose={() => setModal(null)}
        title={modal?.mode === "note" ? `ملاحظة متابعة — ${modal?.school.name}` : `تنبيه المنسقة — ${modal?.school.name}`} testid="sup-action-modal">
        <div className="space-y-3">
          <p className="text-[11px] text-mutext">
            {modal?.mode === "note"
              ? "الملاحظة تُحفظ في سجل المدرسة وتظهر للمدير العام في صفحة المشرفين."
              : "يصل التنبيه فوراً إلى جرس تنبيهات منسقة المدرسة في بوابتها."}
          </p>
          <textarea data-testid="sup-action-text" value={text} onChange={(e) => setText(e.target.value)} rows={4}
            className={`${inputCls} resize-none`} placeholder={modal?.mode === "note" ? "مثال: المدرسة متأخرة في رصد فرص هذا الشهر…" : "مثال: نرجو استكمال رصد ساعات فرصة الأسبوع الماضي…"} />
          <button data-testid="sup-action-send" onClick={() => send(modal.mode)} disabled={busy} className={`${btnTurq} w-full justify-center`}>
            {busy ? "جارٍ الإرسال …" : modal?.mode === "note" ? "حفظ الملاحظة" : "إرسال التنبيه"}
          </button>
        </div>
      </Modal>

      <Modal open={modal?.mode === "notes"} onClose={() => setModal(null)} title={`سجل ملاحظات — ${modal?.school.name || ""}`} testid="sup-notes-modal">
        <div className="space-y-2 max-h-96 overflow-auto">
          {notes.length === 0 && <div data-testid="sup-notes-empty" className="text-center text-mutext text-sm py-8">لا توجد ملاحظات مسجلة لهذه المدرسة بعد</div>}
          {notes.map((n) => (
            <div key={n.id} data-testid={`sup-note-item-${n.id.slice(0, 8)}`} className="rounded-xl border border-brd bg-bg p-3">
              <div className="text-[13px] font-bold leading-relaxed">{n.note}</div>
              <div className="text-[10px] text-mutext mt-1.5">{n.supervisor_name} · {new Date(n.created_at).toLocaleDateString("ar-SA")}</div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
